<template>
  <div class="kanban-board">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <p>Loading cases...</p>
    </div>

    <!-- Kanban Board -->
    <div v-else class="kanban-container">
      <div class="kanban-header">
        <h2>{{ boardTitle }}</h2>
        <div class="bucket-filters">
          <span v-for="bucket in supportedBuckets" :key="bucket.id" 
                :class="['case-type-badge', { 
                  active: selectedBuckets.includes(bucket.id) 
                }]"
                :style="getBucketStyle(bucket.id)"
                @click="toggleBucket(bucket.id)">
            {{ bucket.name }}
          </span>
        </div>
      </div>

      <div class="kanban-columns">
        <div v-for="column in visibleColumns" :key="column.state" 
             class="kanban-column"
             @drop="onDrop($event, column.state)"
             @dragover.prevent
             @dragenter.prevent>
          <div class="column-header">
            <h3>{{ column.state }}</h3>
            <span class="case-count">{{ column.cases.length }}</span>
          </div>
          <div class="column-content">
            <div v-if="column.cases.length === 0" class="empty-column">
              <div class="empty-message">
                <i class="material-icons">inbox</i>
                <p>No cases in this status</p>
              </div>
            </div>
            <div v-for="caseItem in column.cases" :key="`case-${caseItem.id}`"
                 class="case-card"
                 :style="getCaseCardStyle(caseItem)"
                 draggable="true"
                 @dragstart="onDragStart($event, caseItem)"
                 @click="openCase(caseItem)">
              <div class="case-header">
                <div class="case-title">{{ caseItem.title }}</div>
                <div class="case-id">#{{ caseItem.external_ticket_reference }}</div>
              </div>
              
              <div class="case-details">
                <div class="case-description" v-html="caseItem.properties && caseItem.properties.description"></div>
                <div class="case-assignee" v-if="caseItem.properties && caseItem.properties.assigned_to">
                  <i class="material-icons">person</i>
                  <span>{{ caseItem.properties.assigned_to.properties && caseItem.properties.assigned_to.properties.title }}</span>
                </div>
                
                <div class="case-hours" v-if="caseItem.properties && caseItem.properties.remaining_hours !== undefined">
                  <i class="material-icons">schedule</i>
                  <span>{{ caseItem.properties.remaining_hours || 'n/a' }}h</span>
                </div>
              </div>
              
              <div class="case-footer">
                <span class="case-date">{{ formatDate(caseItem.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { provider } from '@/provider.js'

export default {
  name: 'KanbanBoard',
  props: {
    buckets: {
      type: Array,
      required: true
    },
    boardTitle: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      columnOrder: [
        'Not Started',
        'Started',
        'Under Review',
        'Ready for UAT',
        'Ready for Release',
        'Complete'
      ],
      loading: false,
      cases: [],
      selectedBuckets: [],
      caseTypeStatuses: {},
      allUniqueStatuses: []
    }
  },
  computed: {
    ...mapState({
      caseTypes: state => state.caseTypes.all,
      currentUser: state => state.currentUser.me
    }),
    supportedBuckets() {
      return this.buckets
    },
    visibleColumns() {
      console.log('Building visible columns...')
      console.log('Selected buckets:', this.selectedBuckets)
      console.log('Available cases:', this.cases.length)
      console.log('Case type statuses:', this.caseTypeStatuses)
      console.log('All unique statuses:', this.allUniqueStatuses)
      
      // Use the dynamic allUniqueStatuses instead of hardcoded columnOrder
      const statusesToShow = this.allUniqueStatuses.length > 0 ? this.allUniqueStatuses : this.columnOrder
      const orderedColumns = []
      
      statusesToShow.forEach(state => {
        const filteredCases = this.cases.filter(caseItem => {
          const stateMatch = caseItem.currentState === state
          
          // Check if this case belongs to any of the selected buckets
          // We now store bucketId with each case when loading
          const bucketMatch = caseItem.bucketId && this.selectedBuckets.includes(caseItem.bucketId)
          
          return stateMatch && bucketMatch
        })
        
        orderedColumns.push({
          state,
          cases: filteredCases
        })
        
        console.log(`Column "${state}": ${filteredCases.length} cases`)
      })
      
      console.log('Created columns:', orderedColumns.map(col => col.state))
      console.log('Final columns data:', orderedColumns)
      
      return orderedColumns
    }
  },
  watch: {
    caseTypes: {
      handler() {
        console.log('=== CASE TYPES WATCHER TRIGGERED ===')
        console.log('Case types changed:', this.caseTypes)
        this.buildCaseTypeStatuses()
      },
      immediate: true
    },
    selectedBuckets: {
      handler() {
        console.log('=== SELECTED BUCKETS WATCHER TRIGGERED ===')
        console.log('Selected buckets changed:', this.selectedBuckets)
        this.buildCaseTypeStatuses()
      }
    }
  },
  async mounted() {
    // Select all buckets by default
    this.selectedBuckets = this.supportedBuckets.map(b => b.id)
    
    // Dynamically build caseTypeStatuses from supported case types
    this.buildCaseTypeStatuses()
    
    await this.loadCases()
  },
  methods: {
    buildCaseTypeStatuses() {
      // Reset the statuses
      this.caseTypeStatuses = {}
      
      // Get all unique statuses from supported case types
      const allStatuses = new Set()
      console.log('=== BUILDING CASE TYPE STATUSES ===')
      console.log('Available case types:', this.caseTypes)
      console.log('Case types length:', this.caseTypes ? this.caseTypes.length : 'undefined')
      console.log('Supported buckets:', this.supportedBuckets)
      
      this.supportedBuckets.forEach(bucket => {
        const caseType = this.caseTypes.find(ct => ct.id === bucket.caseTypeId)
        console.log(`Looking for case type ${bucket.caseTypeId} for bucket ${bucket.name}:`, caseType)
        
        if (caseType && caseType.statuses) {
          // Store statuses for this case type
          this.caseTypeStatuses[bucket.caseTypeId] = caseType.statuses.map(status => status.state)
          console.log(`Statuses for case type ${bucket.caseTypeId} (${caseType.name}):`, this.caseTypeStatuses[bucket.caseTypeId])
          console.log('Raw statuses from case type:', caseType.statuses)
          
          // Add to global unique list
          caseType.statuses.forEach(status => {
            allStatuses.add(status.state)
          })
        } else {
          console.warn(`No case type or statuses found for bucket ${bucket.name} (case type ID: ${bucket.caseTypeId})`)
          console.log('Case type found:', caseType)
          if (caseType) {
            console.log('Case type properties:', Object.keys(caseType))
            console.log('Case type statuses property:', caseType.statuses)
          }
        }
      })
      
      // Store the unique list of all statuses
      this.allUniqueStatuses = Array.from(allStatuses)
      console.log('All unique statuses:', this.allUniqueStatuses)
      console.log('Final caseTypeStatuses:', this.caseTypeStatuses)
      console.log('=== END BUILDING CASE TYPE STATUSES ===')
    },
    async loadCases() {
      this.loading = true
      try {
        console.log('=== LOADING CASES ===')
        console.log('Selected buckets:', this.selectedBuckets)
        
        const allCases = []
        for (const bucketId of this.selectedBuckets) {
          console.log('Loading cases for bucket:', bucketId)
          const bucket = this.supportedBuckets.find(b => b.id === bucketId)
          console.log('Bucket:', bucket)
          if (bucket) {
            try {
              const bucketData = await provider.findOrderedBucket(bucket.id, 1, 1000)
              
              if (bucketData && bucketData.bucket && bucketData.bucket.contents && bucketData.bucket.contents.cases) {
                const cases = bucketData.bucket.contents.cases
                console.log('Cases:', cases)
                // Add bucket information to each case
                const casesWithBucket = cases.map(caseItem => ({
                  ...caseItem,
                  bucketId: bucket.id,
                  bucketColor: bucket.color
                }))
                allCases.push(...casesWithBucket)
              }
            } catch (bucketError) {
              console.error(`Error loading bucket ${bucket.name}:`, bucketError)
            }
          } else {
            console.warn(`Bucket with ID ${bucketId} not found in supportedBuckets`)
          }
        }
        this.cases = allCases
      } catch (error) {
        console.error('Error loading cases:', error)
      } finally {
        this.loading = false
      }
    },
    toggleBucket(bucketId) {
      const index = this.selectedBuckets.indexOf(bucketId)
      if (index > -1) {
        this.selectedBuckets.splice(index, 1)
      } else {
        this.selectedBuckets.push(bucketId)
      }
    },
    getCaseTypeName(caseTypeId) {
      const caseType = this.caseTypes.find(ct => ct.id === caseTypeId)
      return caseType ? caseType.displayName : 'Unknown'
    },
    getBucketName(caseTypeId) {
      const bucket = this.supportedBuckets.find(b => b.caseTypeId === caseTypeId)
      return bucket ? bucket.caseTypeName : 'Unknown'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    onDragStart(event, caseItem) {
      event.dataTransfer.setData('text/plain', JSON.stringify(caseItem))
    },
    async onDrop(event, targetState) {
      event.preventDefault()
      const caseData = JSON.parse(event.dataTransfer.getData('text/plain'))
      
      // Find the bucket for this case using the bucketId stored with the case
      const bucket = this.supportedBuckets.find(b => b.id === caseData.bucketId)
      if (!bucket) {
        this.$store.commit('displayFlashMessage', {
          message: `No bucket found for case`,
          severity: 'error'
        })
        return
      }
      
      // Validate if the target state is valid for this case type
      const validStates = this.caseTypeStatuses[bucket.caseTypeId] || []
      
      if (!validStates.includes(targetState)) {
        this.$store.commit('displayFlashMessage', {
          message: `Cannot move case to "${targetState}" state. Valid states for this case type are: ${validStates.join(', ')}`,
          severity: 'error'
        })
        return
      }

      try {
        // Update case state - need to update both currentState and properties.current_state
        const updatedProperties = { ...caseData.properties, current_state: targetState }
        
        const updatedCase = await provider.updateCase({
          id: caseData.id,
          title: caseData.title,
          currentState: targetState,
          properties: updatedProperties,
          checkVersion: caseData.version
        })

        if (updatedCase && !updatedCase.error) {
          // Update local case data
          const caseIndex = this.cases.findIndex(c => c.id === caseData.id)
          if (caseIndex > -1) {
            this.cases[caseIndex].currentState = targetState
          }
          
          this.$store.commit('displayFlashMessage', {
            message: `Case moved to "${targetState}"`,
            severity: 'success'
          })
        } else {
          throw new Error(updatedCase && updatedCase.message ? updatedCase.message : 'Failed to update case')
        }
      } catch (error) {
        console.error('Error updating case state:', error)
        this.$store.commit('displayFlashMessage', {
          message: 'Failed to move case',
          severity: 'error'
        })
      }
    },
    openCase(caseItem) {
      this.$router.push({
        name: 'Case',
        params: { case_id: caseItem.id }
      })
    },
    getBucketStyle(bucketId) {
      const bucket = this.supportedBuckets.find(b => b.id === bucketId)
      if (!bucket) return {}
      
      const isActive = this.selectedBuckets.includes(bucketId)
      return {
        backgroundColor: isActive 
          ? bucket.color 
          : '#e0e0e0',
        color: isActive 
          ? 'white' 
          : '#666',
        border: `2px solid ${bucket.color}`
      }
    },
    getCaseCardStyle(caseItem) {
      // Use the bucket information stored with the case
      if (!caseItem.bucketColor || this.selectedBuckets.length === 0) {
        return {}
      }
      
      return { 
        borderBottom: `3px solid ${caseItem.bucketColor}`,
        borderLeft: `3px solid ${caseItem.bucketColor}`,
        borderWidth: '0 0 3px 3px'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.kanban-board {
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kanban-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;

  h2 {
    margin: 0;
    color: #333;
  }

  .bucket-filters {
    display: flex;
    gap: 10px;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  
  p {
    margin-top: 10px;
    color: #666;
  }
}

.kanban-container {
  flex: 1;
  overflow: hidden;
}

.kanban-columns {
  display: flex;
  gap: 20px;
  height: 100%;
  overflow-x: auto;
}

.kanban-column {
  min-width: 300px;
  max-width: 350px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
}

.column-header {
  padding: 15px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .case-count {
    background: #e0e0e0;
    color: #666;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }
}

.column-content {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
  min-height: 200px;
}

.empty-column {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: #999;
  
  .empty-message {
    text-align: center;
    
    .material-icons {
      font-size: 32px;
      margin-bottom: 8px;
      opacity: 0.5;
    }
    
    p {
      margin: 0;
      font-size: 12px;
      opacity: 0.7;
    }
  }
}

.case-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  min-height: 120px;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.case-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex: 1;
}

.case-title {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  margin-right: 8px;
}

.case-id {
  color: #999;
  font-size: 11px;
  font-weight: 500;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.case-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  flex: 1;
}

.case-assignee,
.case-hours {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
  
  .material-icons {
    font-size: 14px;
    color: #999;
  }
}

.case-description {
  color: #666;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;
  max-height: 200px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.case-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.case-type {
  background: #f0f0f0;
  color: #666;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
}

.case-date {
  color: #999;
  font-size: 11px;
  font-weight: 400;
}

.case-type-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
}

// Drag and drop styles
.column-content.drag-over {
  background: rgba(25, 118, 210, 0.1);
  border: 2px dashed #1976d2;
}

.case-card.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}
</style>
